import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextInput,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';
import LandingPageHtmlEditor from './LandingPageHtmlEditor';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 8px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }

    td {
        padding: 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: middle;
    }

    tbody tr.clickable {
        cursor: pointer;
        &:hover {
            background: ${palette.graphite.cloud};
        }
    }

    .actions {
        white-space: nowrap;
        text-align: right;
    }
`;

const PreviewFrame = styled.iframe`
    width: 100%;
    min-height: 360px;
    max-height: 480px;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 4px;
    background: #fff;
`;

const AiPanel = styled.div`
    margin: 16px 0 8px;
    padding: 14px 16px;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    background: ${palette.graphite.white};
`;

const BriefTextarea = styled.textarea`
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: 56px;
    padding: 10px 12px;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.45;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 4px;
    resize: vertical;
`;

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"/><title>Sign in</title></head>
<body style="font-family:sans-serif;max-width:420px;margin:40px auto">
<h2>Sign in</h2>
<form method="POST">
<label>Email<br/><input name="email" type="email" value="{{.Email}}"/></label><br/><br/>
<label>Password<br/><input name="password" type="password"/></label><br/><br/>
<button type="submit">Continue</button>
</form>
</body>
</html>`;

const emptyForm = {
    name: '',
    brand: '',
    htmlBody: DEFAULT_HTML,
    captureCredentials: true,
    redirectUrl: '',
};

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read image'));
        reader.readAsDataURL(file);
    });
}

function AwarePhishLandingPages() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const {
        landingPages,
        loadingLandingPages,
        savingAsset,
        generatingLandingPage,
    } = awarePhishStore;

    const [editingId, setEditingId] = useState(null);
    const [editingPlatform, setEditingPlatform] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editorKey, setEditorKey] = useState(0);
    const [showForm, setShowForm] = useState(false);
    const [showAi, setShowAi] = useState(false);
    const [aiBrief, setAiBrief] = useState('');
    const [screenshotName, setScreenshotName] = useState('');
    const [generateElapsedSec, setGenerateElapsedSec] = useState(0);
    const fileRef = useRef(null);
    const screenshotRef = useRef(null);
    const generateTimerRef = useRef(null);

    useEffect(() => {
        if (companyId) awarePhishStore.loadLandingPages(companyId);
    }, [companyId]);

    const startCreate = () => {
        setEditingId(null);
        setEditingPlatform(false);
        setForm(emptyForm);
        setEditorKey((k) => k + 1);
        setShowAi(false);
        setAiBrief('');
        setScreenshotName('');
        screenshotRef.current = null;
        setShowForm(true);
    };

    const startEdit = (page) => {
        setEditingId(page.id);
        setEditingPlatform(!!page.isPlatform);
        setForm({
            name: page.name || '',
            brand: page.brand || '',
            htmlBody: page.htmlBody || DEFAULT_HTML,
            captureCredentials: page.captureCredentials !== false,
            redirectUrl: page.redirectUrl || '',
        });
        setEditorKey((k) => k + 1);
        setShowAi(false);
        setAiBrief('');
        setScreenshotName('');
        screenshotRef.current = null;
        setShowForm(true);
    };

    const save = async () => {
        try {
            const payload = {
                name: form.name,
                htmlBody: form.htmlBody,
                captureCredentials: !!form.captureCredentials,
                redirectUrl: form.redirectUrl || null,
            };
            const saved = await awarePhishStore.saveLandingPage(
                companyId,
                editingId,
                payload
            );
            commonStore.success(
                editingPlatform
                    ? 'Saved as your company copy'
                    : editingId
                      ? 'Landing page updated'
                      : 'Landing page created'
            );
            setEditingId(saved.id);
            setEditingPlatform(false);
            setShowForm(false);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save landing page'
            );
        }
    };

    const remove = async (page, e) => {
        if (e) e.stopPropagation();
        if (page.isPlatform) return;
        if (!window.confirm(`Delete landing page “${page.name}”?`)) return;
        try {
            await awarePhishStore.removeLandingPage(companyId, page.id);
            commonStore.success('Landing page deleted');
        } catch (err) {
            commonStore.error(
                awarePhishStore.error || 'Failed to delete landing page'
            );
        }
    };

    const onPickScreenshot = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        try {
            const dataUrl = await readFileAsDataUrl(file);
            screenshotRef.current = {
                dataUrl,
                mimeType: file.type || 'image/png',
            };
            setScreenshotName(file.name || 'screenshot');
        } catch (err) {
            commonStore.error('Could not read the screenshot');
        }
    };

    const runAiGenerate = async () => {
        const shot = screenshotRef.current;
        if (!shot || !shot.dataUrl) {
            commonStore.error('Choose a screenshot first');
            return;
        }
        setGenerateElapsedSec(0);
        if (generateTimerRef.current) clearInterval(generateTimerRef.current);
        generateTimerRef.current = setInterval(() => {
            setGenerateElapsedSec((s) => s + 1);
        }, 1000);
        try {
            const draft =
                await awarePhishStore.generateLandingPageFromScreenshot(
                    companyId,
                    {
                        imageBase64: shot.dataUrl,
                        mimeType: shot.mimeType,
                        brief: aiBrief,
                    }
                );
            setForm((prev) => ({
                ...prev,
                name: prev.name || draft.name || '',
                htmlBody: draft.htmlBody || prev.htmlBody,
                redirectUrl: draft.redirectUrl || prev.redirectUrl,
                captureCredentials:
                    draft.captureCredentials != null
                        ? !!draft.captureCredentials
                        : prev.captureCredentials,
            }));
            setEditorKey((k) => k + 1);
            commonStore.success('Draft generated — review before saving');
        } catch (err) {
            const status = err && err.response && err.response.status;
            const msg =
                awarePhishStore.error ||
                (status === 502 || status === 504
                    ? 'Generation timed out or failed. Try a smaller screenshot, skip base64 embeds in guidance, then retry.'
                    : 'Failed to generate landing page');
            commonStore.error(msg);
        } finally {
            if (generateTimerRef.current) {
                clearInterval(generateTimerRef.current);
                generateTimerRef.current = null;
            }
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH landing pages</title>
            </Helmet>
            <HeaderSection
                heading="Landing pages"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Pages shown after a recipient clicks the simulation link. Built-in pages can be customized — edits save as your company copy.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New landing page"
                        mode="primary"
                        onClick={startCreate}
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="landing-pages" />

            {showForm ? (
                <Block padding="20" column style={{ marginBottom: 14 }}>
                    <Heading3>
                        {editingPlatform
                            ? 'Customize built-in page (saves a copy)'
                            : editingId
                              ? 'Edit landing page'
                              : 'New landing page'}
                    </Heading3>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.name}
                        onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                        }
                    />
                    {form.brand ? (
                        <>
                            <FieldLabel>Brand</FieldLabel>
                            <TextDefault>{form.brand}</TextDefault>
                        </>
                    ) : null}

                    <FlexDiv
                        alignCenter
                        gap={12}
                        style={{ marginTop: 16, flexWrap: 'nowrap' }}
                    >
                        <ThemedButton
                            text={
                                showAi
                                    ? 'Hide Generate from screenshot'
                                    : 'Generate from screenshot'
                            }
                            mode="secondary"
                            onClick={() => setShowAi((v) => !v)}
                            $noWrap
                        />
                        <TextTiny lighter>
                            Upload a login-page screenshot; AI drafts HTML you
                            can edit. Usually 30–90 seconds — large images or
                            “embed everything as base64” can take longer or
                            time out.
                        </TextTiny>
                    </FlexDiv>

                    {showAi ? (
                        <AiPanel>
                            <FieldLabel style={{ marginTop: 0 }}>
                                Screenshot
                            </FieldLabel>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                style={{ display: 'none' }}
                                onChange={onPickScreenshot}
                            />
                            <FlexDiv gap={12} alignCenter>
                                <ThemedButton
                                    text="Choose image…"
                                    mode="secondary"
                                    onClick={() =>
                                        fileRef.current &&
                                        fileRef.current.click()
                                    }
                                    $noWrap
                                />
                                <TextTiny lighter>
                                    {screenshotName || 'No file selected'}
                                </TextTiny>
                            </FlexDiv>
                            <FieldLabel>Extra guidance (optional)</FieldLabel>
                            <BriefTextarea
                                value={aiBrief}
                                onChange={(e) => setAiBrief(e.target.value)}
                                placeholder="e.g. Match Microsoft login styling; keep email prefilled"
                            />
                            <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                <ThemedButton
                                    text={
                                        generatingLandingPage
                                            ? `Generating… ${generateElapsedSec}s`
                                            : 'Generate draft'
                                    }
                                    mode="primary"
                                    onClick={runAiGenerate}
                                    disabled={
                                        generatingLandingPage ||
                                        !screenshotName
                                    }
                                    $noWrap
                                />
                                {generatingLandingPage ? (
                                    <TextTiny lighter>
                                        Still working — not frozen while the
                                        counter advances.
                                    </TextTiny>
                                ) : null}
                            </FlexDiv>
                        </AiPanel>
                    ) : null}

                    <FieldLabel>HTML</FieldLabel>
                    <TextTiny lighter style={{ marginBottom: 6 }}>
                        Use a POST form for credential capture. Optional tags:{' '}
                        {'{{.FirstName}}'}, {'{{.Email}}'}. Password values are
                        never stored.
                    </TextTiny>
                    <LandingPageHtmlEditor
                        key={editorKey}
                        editorKey={editorKey}
                        value={form.htmlBody}
                        onChange={(html) =>
                            setForm((prev) => ({ ...prev, htmlBody: html }))
                        }
                    />

                    <FieldLabel>Preview</FieldLabel>
                    <PreviewFrame
                        title="Landing page preview"
                        sandbox=""
                        srcDoc={form.htmlBody || '<p></p>'}
                    />

                    <FieldLabel>Redirect URL after submit (optional)</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.redirectUrl}
                        onChange={(e) =>
                            setForm({ ...form, redirectUrl: e.target.value })
                        }
                        placeholder="https://example.com/training"
                    />

                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 12,
                            fontSize: 13,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={!!form.captureCredentials}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    captureCredentials: e.target.checked,
                                })
                            }
                        />
                        Capture form submissions (recommended)
                    </label>

                    <FlexDiv gap={12} style={{ marginTop: 16 }}>
                        <ThemedButton
                            text={savingAsset ? 'Saving…' : 'Save'}
                            mode="primary"
                            onClick={save}
                            disabled={savingAsset}
                            $noWrap
                        />
                        <ThemedButton
                            text="Cancel"
                            mode="secondary"
                            onClick={() => setShowForm(false)}
                            $noWrap
                        />
                    </FlexDiv>
                </Block>
            ) : null}

            <Block tablePadding column>
                {loadingLandingPages && !(landingPages || []).length ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !(landingPages || []).length ? (
                    <TextDefault lighter>
                        No landing pages yet. Create one or customize a
                        built-in page.
                    </TextDefault>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Brand</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(landingPages || []).map((p) => (
                                <tr
                                    key={p.id}
                                    className="clickable"
                                    onClick={() => startEdit(p)}
                                >
                                    <td>
                                        <TextDefault bold>
                                            {p.name}
                                        </TextDefault>
                                        {p.isPlatform ? (
                                            <div>
                                                <TextTiny lighter>
                                                    Built-in — edit creates your
                                                    copy
                                                </TextTiny>
                                            </div>
                                        ) : null}
                                    </td>
                                    <td>
                                        <TextDefault>
                                            {p.brand || '—'}
                                        </TextDefault>
                                    </td>
                                    <td className="actions">
                                        <FlexDiv
                                            gap={8}
                                            style={{
                                                justifyContent: 'flex-end',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            <ThemedButton
                                                text={
                                                    p.isPlatform
                                                        ? 'Customize'
                                                        : 'Edit'
                                                }
                                                mode="secondary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    startEdit(p);
                                                }}
                                                $noWrap
                                            />
                                            {!p.isPlatform ? (
                                                <ThemedButton
                                                    text="Delete"
                                                    mode="secondary"
                                                    onClick={(e) =>
                                                        remove(p, e)
                                                    }
                                                    $noWrap
                                                />
                                            ) : null}
                                        </FlexDiv>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishLandingPages);
